import axios, { AxiosRequestConfig } from "axios";
import qs from "qs";
import { getBrowserId, saveBrowserIdIfAbsent } from "src/utils/browserId";
import { getPushAttribution } from "src/utils/pushAttribution";
import { linkPushVisitor } from "src/utils/pushification";

// Endpoints that must always carry the persisted browser_id header.
const BROWSER_ID_ENDPOINTS = ["/api/ai/path", "/api/events/create", "/api/events/dwell"];

const requiresBrowserIdHeader = (url?: string): boolean => {
  if (!url) return false;
  return BROWSER_ID_ENDPOINTS.some((endpoint) => url.startsWith(endpoint));
};

const readBrowserIdHeader = (headers?: Record<string, unknown>): string | undefined => {
  if (!headers) return undefined;
  const key = Object.keys(headers).find((k) => k.toLowerCase() === "browser_id");
  return key ? (headers[key] as string) : undefined;
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  paramsSerializer: (params) =>
    qs.stringify(params, {
      arrayFormat: "repeat",
    }),
});

axiosInstance.interceptors.request.use(
  (config: AxiosRequestConfig): AxiosRequestConfig => {
    const raw = localStorage.getItem("kt-auth-react-v");
    if (raw) {
      try {
        const auth: { accessToken?: string } = JSON.parse(raw);
        if (auth?.accessToken) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${auth.accessToken}`,
          };
        }
      } catch (e) {
        console.log(e);
      }
    }

    if (requiresBrowserIdHeader(config.url)) {
      const browserId = getBrowserId();
      if (browserId) {
        config.headers = {
          ...config.headers,
          Browser_ID: browserId,
        };
      }

      // Ziyaret bir bildirimden geldiyse atıf her istekte taşınır; backend bunu
      // oturuma yazar ve oturuma bağlı olaylar push kaynaklı olarak işaretlenir.
      const { notificationId, visitorId } = getPushAttribution();
      if (notificationId || visitorId) {
        config.headers = {
          ...config.headers,
          ...(notificationId ? { Pu_Id: notificationId } : {}),
          ...(visitorId ? { Pu_Vid: visitorId } : {}),
        };
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  function (response) {
    const browserId = readBrowserIdHeader(response?.headers as Record<string, unknown>);
    if (browserId) {
      saveBrowserIdIfAbsent(browserId);
      // SDK, browser_id hazır olmadan yüklenmiş olabilir; o durumda geçici bir
      // kimlikle başlar. Kalıcı kimliği burada bildirip iki tarafı tek kimlikte
      // birleştiriyoruz.
      linkPushVisitor(getBrowserId());
    }
    return response;
  },
  function (error) {
    if (error && error.response && 401 === error.response.status) {
      logout();
      return;
    }
    return error.response;
  },
);

const logout = () => {
  // Only clear auth-related items, preserve impression_counts
  localStorage.removeItem("token");
  localStorage.removeItem("kt-auth-react-v");
  document.location.reload();
};

export default axiosInstance;
